/**
 * Phase 7 Commit J. Confirm step — confirm-hero in display type, paid
 * deposit badge, structured key/value list, brand-color CTA in inline
 * action bar (no RunningBar — Confirm's CTA wants its own brand
 * emphasis + a fineprint cancellation note).
 */

import type { BootstrapResponse, Cart } from '../types';
import { ArrowIcon, BackIcon, CheckIcon } from '../icons';

interface Props {
  cart: Cart;
  locationName: string;
  locationAddress?: string | null;
  timezone: string;
  onConfirm: () => void;
  onBack: () => void;
  submitting: boolean;
  /** Phase 7 — primaryCtaCopy overrides the default first-person CTA. */
  primaryCtaCopy?: string | null;
  /** Phase 7 — show "$X deposit paid" badge when payment was made. */
  depositPaidCents?: number;
  /**
   * The booking's total INCLUDING sales tax, when the salon charges it
   * (Be You). Absent for every other tenant, who keep the cart subtotal.
   */
  totalCents?: number;
  /** Booking profile from bootstrap — drives the dynamic cancellation
   *  policy line so we don't say "the deposit is kept" to a salon that
   *  doesn't take deposits. */
  bookingProfile?: BootstrapResponse['bookingProfile'];
}

/**
 * Build the fineprint cancellation line from bootstrap data, falling back
 * to a sensible generic when fields are missing. Three states:
 *   1. Deposit was paid → references the deposit being kept.
 *   2. No deposit but a cancellation fee exists → references a fee.
 *   3. Neither → just the free-cancel window, no consequence text.
 *
 * Always omits the second sentence entirely when the salon's policy is
 * unknown — better to under-promise than to assert a deposit-keep policy
 * that doesn't apply.
 */
function buildCancellationLine(
  bookingProfile: BootstrapResponse['bookingProfile'] | undefined,
  depositPaidCents: number | undefined,
): string {
  // Default to 24h when Square didn't surface a window.
  const seconds = bookingProfile?.cancellationWindowSeconds ?? 86400;
  const hours = Math.max(1, Math.round(seconds / 3600));
  const windowLabel = hours % 24 === 0 && hours >= 24
    ? `${hours / 24}d`
    : `${hours}h`;

  const head = `Cancel free up to ${windowLabel} before.`;

  if (depositPaidCents && depositPaidCents > 0) {
    return `${head} After that, the deposit is kept toward the stylist's time.`;
  }
  if (bookingProfile?.cancellationFee) {
    return `${head} Late cancellations may be charged a fee.`;
  }
  return head;
}

function formatDate(iso: string | undefined, tz: string): string {
  if (!iso) return 'TBD';
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: tz,
  });
}

function formatTime(iso: string | undefined, tz: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  });
}

function formatPrice(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

export function ConfirmStep({
  cart,
  locationName,
  locationAddress,
  timezone,
  onConfirm,
  onBack,
  submitting,
  primaryCtaCopy,
  depositPaidCents,
  totalCents,
  bookingProfile,
}: Props) {
  const total = totalCents ?? cart.subtotal.amountCents;
  const cancellationLine = buildCancellationLine(bookingProfile, depositPaidCents);
  const line = cart.lines[0];
  const baseLine = cart.lines[0];
  const addOnLines = cart.lines.slice(1);
  const balanceCents = depositPaidCents
    ? Math.max(0, total - depositPaidCents)
    : 0;

  const ctaLabel =
    primaryCtaCopy && primaryCtaCopy.trim().length > 0
      ? primaryCtaCopy
      : 'Book my appointment';

  return (
    <div class="sbw-scroll sbw-confirm">
      <div class="sbw-eyebrow">One last look</div>
      <h2 class="sbw-step-title">You&apos;re a tap from booked.</h2>
      <p class="sbw-step-sub">
        Check the details — then lock it in below.
      </p>

      <div class="sbw-confirm-hero">
        <div class="sbw-eyebrow" style={{ marginBottom: 6 }}>
          {formatDate(line?.startAt, timezone)}
        </div>
        <h3 class="sbw-confirm-when">
          {formatTime(line?.startAt, timezone)}
          {line?.staffName ? ` with ${line.staffName}` : ''}
          <br />
          at {locationName}
        </h3>
        {locationAddress && (
          <div class="sbw-confirm-with">{locationAddress}</div>
        )}
        {depositPaidCents && depositPaidCents > 0 && (
          <span class="sbw-paid-badge">
            <CheckIcon size={11} />{' '}
            {formatPrice(depositPaidCents, cart.subtotal.currency)} deposit paid
          </span>
        )}
      </div>

      <div class="sbw-confirm-list">
        <div class="sbw-confirm-row">
          <span class="sbw-k">Service</span>
          <span class="sbw-v">
            {baseLine?.serviceName}
            {addOnLines.length > 0 && (
              <>
                <br />
                <span
                  style={{
                    color: 'var(--sbw-ink-3)',
                    fontWeight: 400,
                    fontSize: 11.5,
                  }}
                >
                  + {addOnLines.map((l) => l.serviceName).join(', ')}
                </span>
              </>
            )}
          </span>
        </div>
        <div class="sbw-confirm-row">
          <span class="sbw-k">Total</span>
          <span class="sbw-v">
            {formatPrice(total, cart.subtotal.currency)}
            {balanceCents > 0 && (
              <>
                <br />
                <span
                  style={{
                    color: 'var(--sbw-ink-3)',
                    fontWeight: 400,
                    fontSize: 11,
                  }}
                >
                  {formatPrice(balanceCents, cart.subtotal.currency)} due at
                  appointment
                </span>
              </>
            )}
          </span>
        </div>
        <div class="sbw-confirm-row">
          <span class="sbw-k">Customer</span>
          <span class="sbw-v">
            {cart.client?.givenName}
            {cart.client?.phone ? ` · ${cart.client.phone}` : ''}
          </span>
        </div>
      </div>

      <p class="sbw-fineprint">{cancellationLine}</p>

      <div class="sbw-actionbar">
        <button
          type="button"
          class="sbw-btn sbw-btn--ghost"
          onClick={onBack}
          disabled={submitting}
          aria-label="Go back"
        >
          <BackIcon />
        </button>
        <button
          type="button"
          class="sbw-btn sbw-btn--brand"
          onClick={onConfirm}
          disabled={submitting}
        >
          {submitting ? 'Booking…' : ctaLabel}
          {!submitting && <ArrowIcon />}
        </button>
      </div>
    </div>
  );
}
